"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

function deduplicateLines(
  text: string,
  caseInsensitive: boolean,
  removeBlank: boolean
): {
  output: string;
  totalLines: number;
  uniqueLines: number;
  removedCount: number;
  duplicateLines: string[];
} {
  if (text.length === 0) {
    return {
      output: "",
      totalLines: 0,
      uniqueLines: 0,
      removedCount: 0,
      duplicateLines: [],
    };
  }

  const lines = text.split("\n");
  const seen = new Set<string>();
  const kept: string[] = [];
  const duplicateLines: string[] = [];

  for (const line of lines) {
    if (removeBlank && line.trim().length === 0) {
      continue;
    }
    const key = caseInsensitive ? line.toLowerCase() : line;
    if (seen.has(key)) {
      if (!duplicateLines.includes(line)) {
        duplicateLines.push(line);
      }
    } else {
      seen.add(key);
      kept.push(line);
    }
  }

  const totalLines = lines.filter(
    (l) => !(removeBlank && l.trim().length === 0)
  ).length;
  const uniqueLines = kept.length;
  const removedCount = totalLines - uniqueLines;

  return {
    output: kept.join("\n"),
    totalLines,
    uniqueLines,
    removedCount,
    duplicateLines,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const REMOVE_DUPLICATE_LINES_STRINGS = {
  options: "Options",
  caseInsensitiveTreatAppleAnd:
    'Case-insensitive (treat "Apple" and "apple" as duplicates)',
  alsoRemoveBlankLines: "Also remove blank lines",
  inputText: "Input Text",
  totalLines: "Total Lines",
  uniqueLines: "Unique Lines",
  duplicatesRemoved: "Duplicates Removed",
  cleanedOutput: "Cleaned Output",
  copied: "Copied!",
  copyToClipboard: "Copy to Clipboard",
  removedDuplicates: "Removed Duplicates",
  blankLine: "(blank line)",
  and: "…and ",
  more: " more",
  pasteYourTextHerePlaceholder:
    "Paste your text here — one item per line...\napple\nbanana\napple\ncherry",
} as const;

export default function RemoveDuplicateLinesWidget() {
  const [input, setInput] = useState("");
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [removeBlank, setRemoveBlank] = useState(false);
  const [copied, setCopied] = useState(false);

  const result = deduplicateLines(input, caseInsensitive, removeBlank);

  function handleCopy() {
    if (result.output.length === 0) {
      return;
    }
    writeToClipboard(result.output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const strings = useToolUi(REMOVE_DUPLICATE_LINES_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      <div className="space-y-3 rounded-lg border border-border bg-card p-5">
        <h2 className="font-medium text-foreground text-sm">
          {strings.options}
        </h2>
        <div className="flex flex-wrap gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              checked={caseInsensitive}
              className="accent-primary"
              onChange={(e) => setCaseInsensitive(e.target.checked)}
              type="checkbox"
            />
            <span className="text-foreground">
              {strings.caseInsensitiveTreatAppleAnd}
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              checked={removeBlank}
              className="accent-primary"
              onChange={(e) => setRemoveBlank(e.target.checked)}
              type="checkbox"
            />
            <span className="text-foreground">
              {strings.alsoRemoveBlankLines}
            </span>
          </label>
        </div>
      </div>
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="input-lines"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="input-lines"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.pasteYourTextHerePlaceholder}
          value={input}
        />
      </div>
      {input.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard
              label={strings.totalLines}
              value={fmt(result.totalLines)}
            />
            <StatCard
              label={strings.uniqueLines}
              value={fmt(result.uniqueLines)}
            />
            <StatCard
              label={strings.duplicatesRemoved}
              value={fmt(result.removedCount)}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-foreground text-sm">
                {strings.cleanedOutput}
              </p>
              <button
                className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                disabled={result.output.length === 0}
                onClick={handleCopy}
                type="button"
              >
                {copied ? strings.copied : strings.copyToClipboard}
              </button>
            </div>
            <textarea
              className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-muted/40 p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none"
              readOnly
              value={result.output}
            />
          </div>

          {result.duplicateLines.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold text-foreground text-lg">
                {strings.removedDuplicates}
              </h2>
              <div className="rounded-lg border border-border bg-card p-4">
                <ul className="space-y-1">
                  {result.duplicateLines.slice(0, 20).map((line, i) => (
                    <li
                      className="font-mono text-muted-foreground text-sm"
                      // biome-ignore lint/suspicious/noArrayIndexKey: deduplicated display list, index is stable
                      key={i}
                    >
                      {line.length === 0 ? (
                        <em className="text-muted-foreground/50 not-italic">
                          {strings.blankLine}
                        </em>
                      ) : (
                        line
                      )}
                    </li>
                  ))}
                  {result.duplicateLines.length > 20 && (
                    <li className="text-muted-foreground text-sm">
                      {strings.and}
                      {result.duplicateLines.length - 20}
                      {strings.more}
                    </li>
                  )}
                </ul>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
